/* Returns — the read-only returns list (/sales/returns). Spec/03 archetype A, mirroring
   `orders/List.js` structurally and by decision (same filter/URL/columns/segments machinery) —
   see that file for the reasoning behind each piece; this file only calls out what differs.

   Field shapes come from the ACTUAL `excalibur/returns/` list response: `point`/`supervisor`/
   `visit` are nested objects or `null` on a return with no visit, same dash every other empty
   field gets. There is no money anywhere on this endpoint (see `ReturnDetail.js`'s own note) —
   "Позицій"/"Одиниць" (`items_count`/`units_count`) stand in for the "Сума" column orders has.

   A row click opens the record modal, same as `orders/List.js` — not a navigation. The URL is
   deliberately left untouched while it is open (RET-4 asks for a `pushState`, but orders' own
   modal does not do that either, and the two are kept in lockstep on purpose until that is
   decided for both at once). The row's `⋮` menu is the same one orders carries — a second,
   quicker way to the full page that does not require opening the modal first — kept in lockstep
   with orders for the same reason the modal is. */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Card, Checkbox, Col, Dropdown, Flex, Form, Input, Row, Segmented, Table, Typography } from "antd";
import { ExportOutlined, LinkOutlined, PushpinOutlined, SearchOutlined, ShopOutlined, TableOutlined, TeamOutlined, UserOutlined } from "@ant-design/icons";

import EntitySelect from "../../component/EntitySelect";
import FilterChips, { useLabelCache } from "../../component/FilterChips";
import PeriodPicker from "../../component/PeriodPicker";
import {
  PersonCell,
  RowActions,
  StatusIcon,
  TableFoot,
  TitleCell,
  actionsColumn,
  dash,
  statusColumn,
  textOf,
  useTableScrollY,
} from "../../component/table-cells";
import { PHOTO_REPORT, RETURNS_PAGE } from "../../config/lang";
import { call, isOk } from "../../util/action";
import dayjs from "../../util/dayjs";
import { translate } from "../../util/util";
import { localeFor } from "../merchandiser/route/dateUtils";

import ReturnModal from "./ReturnModal";
import { STATUS_ICON } from "./parts";

/* Same as `orders/List.js`: matches the backend's `StandardResultsSetPagination` default (25). */
const PAGE_SIZES = [10, 25, 50, 100];

/* Six fields (period, counterparty, points, author, supervisor, search) — same row `orders/List.js`
   has. A multi-select Status field lived here briefly, alongside the `Segmented` below; it was
   removed (deliberately: several statuses at once is no longer choosable) so the segments are the
   ONE control for status again, matching orders. */
const FIELD_COL = { xs: { flex: "0 0 100%" }, sm: { flex: "0 0 50%" }, md: { flex: "0 0 33.3333%" }, xl: { flex: "0 0 16.6667%" } };

/* Segment values, "all" plus the same five keys `STATUS_ICON` carries. No counts on any segment
   (project-wide rule). */
const STATUS_SEGMENTS = ["all", ...Object.keys(STATUS_ICON)];

/** Which RETURNS_PAGE key names each toggleable column in the Columns panel — same shape as
    `orders/List.js`'s `COLUMN_LABEL`. The status icon and "Повернення" (the only thing that
    identifies a row) are absent on purpose: a column missing here never appears in the checkbox
    list, which is how a column stays mandatory with no disabled-checkbox state to manage. No
    persistence, matching the precedent exactly. */
const COLUMN_LABEL = {
  counterparty: "k_counterparty",
  point: "k_point",
  created_by: "k_created_by",
  supervisor: "k_supervisor",
  visit: "k_visit",
  items_count: "k_items_count",
  units_count: "k_units_count",
};
const TOGGLEABLE = Object.keys(COLUMN_LABEL);

/* Column key → the field the API orders on. Deliberately no `datetime` entry: verified against the
   local backend that `ordering=datetime` genuinely works, but there is no second sortable column
   for it to attach to (the date lives inside "Повернення", the same visual cell as `name`) and no
   precedent anywhere in the app for one column driving two independent sort keys — every other
   sortable list is one column, one key. `name` is what the header click sorts, same split
   `orders/List.js` already made for its own title column; absent any explicit sort, the backend's
   own default (`-datetime`, confirmed empirically) applies, same as orders. `supervisor` is
   absent for the same M2M-duplication reason orders' is. */
const ORDERING = { name: "name", point: "visit__point__name" };

const idOf = v => (v && typeof v === "object" ? v.id ?? v.value : v);
const joinIds = list =>
  (list || [])
    .map(idOf)
    .filter(v => v !== undefined && v !== null && v !== "")
    .join(",");
const isoDay = d => dayjs(d).format("YYYY-MM-DD");

/* Same as `orders/List.js`: a week, today inclusive, applied on the FRONTEND (see that file's own
   note) — the product owner chose this over the written spec's 30 days, deliberately. NOT the
   shared `defaultRange()` from `PeriodPicker.js` (14 days). */
const defaultRange = () => [dayjs().subtract(7, "day"), dayjs()];

/** The request the table runs on. Both date ends are inclusive on the backend, matching
    `orders/List.js`'s own `buildQuery` — verified against the local backend that `point=` (not
    the plural `points=`), `created_by=`, `supervisor=`, `counterparty=` and `status=` all filter
    correctly, and that `search=` matches the return name and the counterparty name (not
    `comments`). `status` is a single value now — the segments are its only control. */
function buildQuery({ range, counterparty, pointFilter, status, createdBy, supervisorFilter, search, sort, page, size }) {
  const p = new URLSearchParams();
  p.set("page", String(page));
  p.set("page_size", String(size));
  if (range?.[0]) p.set("date_from", isoDay(range[0]));
  if (range?.[1]) p.set("date_to", isoDay(range[1]));
  if (counterparty?.length) p.set("counterparty", joinIds(counterparty));
  if (pointFilter?.length) p.set("point", joinIds(pointFilter));
  if (status) p.set("status", status);
  if (createdBy?.length) p.set("created_by", joinIds(createdBy));
  if (supervisorFilter?.length) p.set("supervisor", joinIds(supervisorFilter));
  if (search) p.set("search", search);
  if (sort) p.set("ordering", `${sort.dir === "descend" ? "-" : ""}${ORDERING[sort.key]}`);
  return p.toString();
}

/** URL ↔ state sync — deliberately local/private, same reasoning as `orders/List.js`: nothing
    outside this file needs `buildQuery`/`searchToState` (no queue-stepping detail page). Array
    filters are comma-joined in the URL, the SAME encoding `buildQuery` sends the backend, and the
    escaped `%2C` is un-escaped again before the string is pushed, exactly as orders does. */
/* ⚠ A cleared period writes `period=none`, not nothing — see `orders/List.js`'s own note, word for
   word the same mechanism: without it, clearing the period while nothing else is active leaves
   `location.search` empty, indistinguishable from a genuinely fresh visit on the next load. */
function stateToSearch({ range, counterparty, pointFilter, status, createdBy, supervisorFilter, search, sort, page, size }) {
  const p = new URLSearchParams();
  if (range) {
    p.set("date_from", isoDay(range[0]));
    p.set("date_to", isoDay(range[1]));
  } else {
    p.set("period", "none");
  }
  if (counterparty?.length) p.set("counterparty", joinIds(counterparty));
  if (pointFilter?.length) p.set("point", joinIds(pointFilter));
  if (status) p.set("status", status);
  if (createdBy?.length) p.set("created_by", joinIds(createdBy));
  if (supervisorFilter?.length) p.set("supervisor", joinIds(supervisorFilter));
  if (search) p.set("search", search);
  if (page > 1) p.set("page", String(page));
  if (size !== PAGE_SIZES[1]) p.set("size", String(size));
  if (sort) p.set("sort", `${sort.dir === "descend" ? "-" : ""}${sort.key}`);
  return p.toString().replace(/%2C/g, ",");
}

function searchToState(search) {
  const p = new URLSearchParams(search);
  const dateFrom = p.get("date_from");
  const dateTo = p.get("date_to");
  const sort = p.get("sort");
  const size = Number(p.get("size"));
  return {
    /* Default applies ONLY on a genuinely bare URL, and a `period=none` marker (see `stateToSearch`
       above) keeps a fully-cleared URL non-empty so it never reads as "fresh visit" on reload —
       see `orders/List.js`'s own note on this, word for word the same reasoning. */
    range: dateFrom && dateTo ? [dayjs(dateFrom), dayjs(dateTo)] : search ? null : defaultRange(),
    counterparty: (p.get("counterparty") || "")
      .split(",")
      .filter(Boolean)
      .map(id => ({ id })),
    pointFilter: (p.get("point") || "")
      .split(",")
      .filter(Boolean)
      .map(id => ({ id: Number(id) })),
    /* `status` is a single value now (the segments are its only control), but an OLD link from
       when it was a multi-select can still carry a comma-joined list (`?status=new,closed`) —
       take just the first token rather than pass the raw, unmatched string through to the
       backend, which would silently return zero rows instead of a sane one-status filter. */
    status: (p.get("status") || "").split(",")[0] || null,
    createdBy: (p.get("created_by") || "")
      .split(",")
      .filter(Boolean)
      .map(id => ({ id: Number(id) })),
    supervisorFilter: (p.get("supervisor") || "")
      .split(",")
      .filter(Boolean)
      .map(id => ({ id: Number(id) })),
    search: p.get("search") || "",
    page: Math.max(1, Number(p.get("page")) || 1),
    size: PAGE_SIZES.includes(size) ? size : PAGE_SIZES[1],
    sort: sort && ORDERING[sort.replace(/^-/, "")] ? { key: sort.replace(/^-/, ""), dir: sort[0] === "-" ? "descend" : "ascend" } : null,
  };
}

/* A link from a counterparty (its card, a return's counterparty block) hands the name over in route
   state, since the URL carries the id only and `EntitySelect` resolves names from its first page
   of options alone — a counterparty past it showed as its UUID in the chip and the field. A
   self-named `{id, name}` labels both without a request. Only for the exact id it was sent for. */
function withStateNames(state, routeState) {
  const { counterpartyId, counterpartyName } = routeState || {};
  if (!counterpartyId || !counterpartyName) return state;
  return {
    ...state,
    counterparty: state.counterparty.map(v => (String(v.id) === String(counterpartyId) ? { id: v.id, name: counterpartyName } : v)),
  };
}

const List = () => {
  const history = useHistory();
  const location = useLocation();
  const lang = useSelector(state => state.me?.language || "en");
  const t = key => translate(RETURNS_PAGE, key, lang);
  const tp = key => translate(PHOTO_REPORT, key, lang);

  const initial = useRef(withStateNames(searchToState(location.search), location.state)).current;

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(initial.page);
  const [size, setSize] = useState(initial.size);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [range, setRange] = useState(initial.range);
  const [counterparty, setCounterparty] = useState(initial.counterparty);
  const [pointFilter, setPointFilter] = useState(initial.pointFilter);
  const [status, setStatus] = useState(initial.status);
  const [createdBy, setCreatedBy] = useState(initial.createdBy);
  const [supervisorFilter, setSupervisorFilter] = useState(initial.supervisorFilter);
  const [typed, setTyped] = useState(initial.search);
  const [search, setSearch] = useState(initial.search);
  const [sort, setSort] = useState(initial.sort);

  const [hidden, setHidden] = useState([]);

  const narrow = fn => (...args) => {
    setPage(1);
    fn(...args);
  };

  const resetFilters = narrow(() => {
    setRange(defaultRange());
    setCounterparty([]);
    setPointFilter([]);
    setStatus(null);
    setCreatedBy([]);
    setSupervisorFilter([]);
    setTyped("");
    setSearch("");
  });

  /* The `Segmented` is the ONLY control for status now (the multi-select field was removed —
     several statuses at once is no longer choosable), same as `orders/List.js`. */
  const segment = status || "all";
  const setSegment = narrow(value => setStatus(value === "all" ? null : value));

  const { labels, remember, nameFor } = useLabelCache("returns:labels");

  const clearChip = narrow((key, id) => {
    if (key === "period") {
      // Clears, same as the picker's own "x" — a distinct action from "Скинути", which goes to
      // the default week instead.
      setRange(null);
      return;
    }
    if (key === "search") {
      setTyped("");
      setSearch("");
      return;
    }
    if (key === "counterparty") {
      setCounterparty(prev => prev.filter(v => String(idOf(v)) !== String(id)));
      return;
    }
    if (key === "point") {
      setPointFilter(prev => prev.filter(v => String(idOf(v)) !== String(id)));
      return;
    }
    if (key === "createdBy") {
      setCreatedBy(prev => prev.filter(v => String(idOf(v)) !== String(id)));
      return;
    }
    setSupervisorFilter(prev => prev.filter(v => String(idOf(v)) !== String(id)));
  });

  const chips = useMemo(() => {
    const out = [];
    if (range?.[0] && range?.[1]) {
      const thisYear = new Date().getFullYear();
      const sameYear = range[0].year() === thisYear && range[1].year() === thisYear;
      const fmt = new Intl.DateTimeFormat(localeFor(lang), { day: "numeric", month: "short", ...(sameYear ? {} : { year: "numeric" }) });
      out.push({ key: "period", label: t("period"), value: `${fmt.format(range[0].toDate())} → ${fmt.format(range[1].toDate())}` });
    }
    counterparty.forEach(v => out.push({ key: "counterparty", id: idOf(v), label: t("k_counterparty"), value: nameFor("counterparty", v) }));
    pointFilter.forEach(v => out.push({ key: "point", id: idOf(v), label: t("k_point"), value: nameFor("point", v) }));
    createdBy.forEach(v => out.push({ key: "createdBy", id: idOf(v), label: t("k_created_by"), value: nameFor("createdBy", v) }));
    supervisorFilter.forEach(v => out.push({ key: "supervisor", id: idOf(v), label: t("k_supervisor"), value: nameFor("supervisor", v) }));
    /* No chip for status — same call `orders/List.js` makes: it has no control of its own beyond
       the `Segmented` below, which already shows the active status by itself. */
    if (search) out.push({ key: "search", label: tp("search"), value: search });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, counterparty, pointFilter, createdBy, supervisorFilter, search, labels, lang]);

  useEffect(() => {
    if (typed === search) return undefined;
    const timer = setTimeout(() => {
      setSearch(typed);
      setPage(1);
    }, 250);
    return () => clearTimeout(timer);
  }, [typed, search]);

  useEffect(() => {
    const nextSearch = stateToSearch({ range, counterparty, pointFilter, status, createdBy, supervisorFilter, search, sort, page, size });
    if (nextSearch !== location.search.replace(/^\?/, "")) history.replace({ search: nextSearch, state: location.state });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, counterparty, pointFilter, status, createdBy, supervisorFilter, search, sort, page, size]);

  const query = useMemo(() => buildQuery({ range, counterparty, pointFilter, status, createdBy, supervisorFilter, search, sort, page, size }), [
    range,
    counterparty,
    pointFilter,
    status,
    createdBy,
    supervisorFilter,
    search,
    sort,
    page,
    size,
  ]);

  const load = useCallback(() => {
    setLoading(true);
    call({ method: "GET", url: `excalibur/returns/?${query}` }, response => {
      if (!isOk(response)) {
        setRows([]);
        setTotal(0);
        setError(true);
        setLoading(false);
        return;
      }
      setRows(response.results || []);
      setTotal(response.count || 0);
      setError(false);
      setLoading(false);
    });
  }, [query]);

  useEffect(load, [load]);

  /* The record modal — `ReturnModal`, shared with the counterparty card's Returns tab. Deliberately
     not URL-integrated (see the file-head note). */
  const [modalId, setModalId] = useState(null);
  const closeModal = () => setModalId(null);

  const scrollY = useTableScrollY();

  const columns = [
    statusColumn((value, row) => {
      const { Icon, tone } = STATUS_ICON[row.status] || STATUS_ICON.new;
      const label = translate(RETURNS_PAGE, `return_status_${row.status}`, lang) || row.status;
      return <StatusIcon Icon={Icon} tone={tone} label={label} />;
    }),
    {
      key: "name",
      title: t("list_title"),
      width: 220,
      fixed: "left",
      ellipsis: true,
      sorter: true,
      sortOrder: sort?.key === "name" ? sort.dir : undefined,
      /* Sort arrow is for the return NUMBER only — same call `orders/List.js` made for its own
         title column. The date underneath has no sort control of its own (see the `ORDERING` note
         above). */
      render: (value, row) => <TitleCell title={row.name} sub={row.datetime ? dayjs(row.datetime).format("DD.MM.YYYY HH:mm") : null} />,
    },
    {
      key: "counterparty",
      title: t("k_counterparty"),
      width: 220,
      ellipsis: true,
      render: (value, row) => textOf(row.counterparty?.name),
    },
    {
      key: "point",
      title: t("k_point"),
      width: 220,
      ellipsis: true,
      sorter: true,
      sortOrder: sort?.key === "point" ? sort.dir : undefined,
      /* Same single-line "name — address" composition as `orders/List.js`'s own point column, not
         `TitleCell` — that stacked shape belongs to "Повернення" alone. */
      render: (value, row) =>
        row.point ? (
          <Typography.Text ellipsis={{ tooltip: [row.point.name, row.point.address].filter(Boolean).join(" — ") }}>
            <Typography.Text strong>{row.point.name}</Typography.Text>
            {row.point.address ? <Typography.Text type="secondary"> — {row.point.address}</Typography.Text> : null}
          </Typography.Text>
        ) : (
          dash
        ),
    },
    {
      key: "created_by",
      title: t("k_created_by"),
      width: 190,
      ellipsis: true,
      render: (value, row) => (row.created_by ? <PersonCell user={row.created_by} strong={false} /> : dash),
    },
    {
      key: "supervisor",
      title: t("k_supervisor"),
      width: 190,
      ellipsis: true,
      render: (value, row) => (row.supervisor ? <PersonCell user={row.supervisor} strong={false} /> : dash),
    },
    {
      key: "visit",
      title: t("k_visit"),
      width: 120,
      render: (value, row) =>
        row.visit ? (
          <Typography.Link
            onClick={event => {
              // The row itself navigates to the return; this link goes somewhere else.
              event.stopPropagation();
              history.push(`/visit/${row.visit}`);
            }}>
            {t("k_visit")} <LinkOutlined />
          </Typography.Link>
        ) : (
          dash
        ),
    },
    {
      key: "items_count",
      title: t("k_items_count"),
      width: 100,
      align: "right",
      render: (value, row) => <span className="lp-num">{textOf(row.items_count)}</span>,
    },
    {
      key: "units_count",
      title: t("k_units_count"),
      width: 100,
      align: "right",
      render: (value, row) => <span className="lp-num">{textOf(row.units_count)}</span>,
    },
    actionsColumn((value, row) => (
      <RowActions
        label={t("row_actions")}
        items={[{ key: "open", icon: <ExportOutlined />, label: t("open_page_btn") }]}
        // Carries the filtered view's URL along, same as `orders/List.js`, so the page's own
        // "До списку" can restore it.
        onClick={() => history.push({ pathname: `/sales/returns/${row.id}`, search: location.search })}
      />
    )),
  ].filter(column => !hidden.includes(column.key));

  return (
    <Flex vertical gap="middle" className="shell-content lp">
      {/* No action beside the title. There was an "Експортувати" button here that called
          `window.print()` — the label promised a file and the control opened a print dialog, over
          a page with no `@media print` block, so what it actually produced was the app chrome.
          `/photo-review`'s Export is the same call but backed by real print styles; until this
          page has those, no button is better than a wrong one. */}
      <Flex className="lp-head" justify="space-between" align="flex-end" gap="middle" wrap>
        <Flex vertical>
          <Typography.Title level={1} className="lp-title">
            {t("list_title")}
          </Typography.Title>
          <Typography.Paragraph type="secondary" className="lp-sub">
            {t("page_sub")}
          </Typography.Paragraph>
        </Flex>
      </Flex>

      <Card className="lp-filters" variant="outlined">
        <Form layout="vertical" component={false} colon={false}>
          <Row gutter={[12, 12]}>
            <Col {...FIELD_COL}>
              <Form.Item label={t("period")}>
                {/* No `clearTo` — the picker's own "x" genuinely clears (range: null, all time),
                    distinct from "Скинути", which goes to the default week. `allowClear` is
                    already unconditional in `PeriodPicker.js` itself. */}
                <PeriodPicker value={range} onChange={narrow(setRange)} />
              </Form.Item>
            </Col>
            <Col {...FIELD_COL}>
              <Form.Item label={t("k_counterparty")}>
                <EntitySelect
                  entity="counterparty"
                  isMulti
                  value={counterparty}
                  prefix={<ShopOutlined />}
                  onChange={narrow(setCounterparty)}
                  onResolve={remember("counterparty")}
                />
              </Form.Item>
            </Col>
            <Col {...FIELD_COL}>
              <Form.Item label={t("k_point")}>
                <EntitySelect
                  entity="points"
                  isMulti
                  value={pointFilter}
                  prefix={<PushpinOutlined />}
                  onChange={narrow(setPointFilter)}
                  onResolve={remember("point")}
                />
              </Form.Item>
            </Col>
            <Col {...FIELD_COL}>
              <Form.Item label={t("k_created_by")}>
                <EntitySelect
                  entity="created_by"
                  isMulti
                  value={createdBy}
                  prefix={<UserOutlined />}
                  onChange={narrow(setCreatedBy)}
                  onResolve={remember("createdBy")}
                />
              </Form.Item>
            </Col>
            <Col {...FIELD_COL}>
              <Form.Item label={t("k_supervisor")}>
                <EntitySelect
                  entity="supervisor"
                  isMulti
                  value={supervisorFilter}
                  prefix={<TeamOutlined />}
                  onChange={narrow(setSupervisorFilter)}
                  onResolve={remember("supervisor")}
                />
              </Form.Item>
            </Col>
            <Col {...FIELD_COL}>
              <Form.Item label={tp("search")}>
                <Input
                  allowClear
                  prefix={<SearchOutlined />}
                  placeholder={t("search_placeholder")}
                  value={typed}
                  onChange={event => setTyped(event.target.value)}
                />
              </Form.Item>
            </Col>
          </Row>

          <FilterChips className="lp-chips" items={chips} closeLabel={tp("a_remove_filter")} onClose={clearChip} />

          <Flex className="lp-filters__foot" align="center" gap="small" wrap>
            <Segmented
              value={segment}
              onChange={setSegment}
              options={STATUS_SEGMENTS.map(value => ({
                value,
                label: value === "all" ? t("seg_all") : translate(RETURNS_PAGE, `return_status_${value}`, lang),
              }))}
            />
            <span className="lp-spacer" />
            <Button size="small" onClick={resetFilters}>
              {t("filter_reset")}
            </Button>
          </Flex>
        </Form>
      </Card>

      <Card className="lp-table" variant="outlined">
        <Flex className="lp-table__bar" align="center" gap="small" wrap>
          <Typography.Text strong className="lp-table__title">
            {t("list_title")}
          </Typography.Text>
          <Typography.Text type="secondary" aria-live="polite">
            {t("records_count")}: <Typography.Text strong>{total}</Typography.Text>
          </Typography.Text>
          <span className="lp-spacer" />
          <Dropdown
            trigger={["click"]}
            popupRender={() => (
              <Card className="lp-colcfg" variant="outlined" size="small">
                <Checkbox.Group
                  value={TOGGLEABLE.filter(key => !hidden.includes(key))}
                  onChange={visible => setHidden(TOGGLEABLE.filter(key => !visible.includes(key)))}>
                  <Flex vertical gap={4}>
                    {TOGGLEABLE.map(key => (
                      <Checkbox key={key} value={key}>
                        {t(COLUMN_LABEL[key])}
                      </Checkbox>
                    ))}
                  </Flex>
                </Checkbox.Group>
              </Card>
            )}>
            <Button size="small" icon={<TableOutlined />}>
              {t("columns")}
            </Button>
          </Dropdown>
        </Flex>

        <Table
          className="lp-table__grid"
          rowKey="id"
          size="middle"
          columns={columns}
          dataSource={rows}
          loading={loading}
          scroll={{ x: 1080, y: scrollY }}
          rowClassName="lp-row"
          onRow={row => ({
            onClick: event => {
              // The visit link stops its own propagation already; everything else opens the
              // record modal.
              if (event.target.closest(".ant-dropdown, .ant-btn")) return;
              setModalId(row.id);
            },
          })}
          onChange={(pagination, filters, sorter) => {
            const next = sorter?.order && ORDERING[sorter.columnKey] ? { key: sorter.columnKey, dir: sorter.order } : null;
            if (JSON.stringify(next) === JSON.stringify(sort)) return;
            setSort(next);
            setPage(1);
          }}
          pagination={false}
          locale={{
            emptyText: error ? (
              <Flex vertical gap={4} align="center">
                <Typography.Text strong>{t("error_title")}</Typography.Text>
                <Button size="small" onClick={load}>
                  {t("retry")}
                </Button>
              </Flex>
            ) : (
              <Flex vertical gap={4} align="center">
                <Typography.Text strong>{t("empty_title")}</Typography.Text>
                <Typography.Text type="secondary">{t("empty_desc")}</Typography.Text>
              </Flex>
            ),
          }}
        />
        <TableFoot
          id="ret"
          page={page}
          size={size}
          total={total}
          sizes={PAGE_SIZES}
          labels={{ perPage: t("rows_per_page"), range: t("range_of") }}
          onPage={setPage}
          onSize={next => {
            setSize(next);
            setPage(1);
          }}
        />
      </Card>

      {/* "Open page" carries the filtered view's URL along, so the page's own "До списку" can restore it. */}
      <ReturnModal id={modalId} lang={lang} onClose={closeModal} openTo={{ pathname: `/sales/returns/${modalId}`, search: location.search }} />
    </Flex>
  );
};

export default List;
